import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Shield, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  UserPlus,
  Eye,
  EyeOff,
  Loader2
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { CustomTabs } from '@/components/ui/custom-tabs'
import { useAdmin, type Igreja, type UsuarioAdmin, type TipoPermissao } from '@/hooks/use-admin'
const PERMISSAO_LABELS: Record<NonNullable<TipoPermissao>, string> = {
  admin: 'Administrador',
  gestor: 'Gestor',
  lider: 'Líder',
  membro: 'Membro'
}

const PERMISSAO_STYLES: Record<NonNullable<TipoPermissao>, string> = {
  admin: 'bg-red-500/15 text-red-600 ring-1 ring-inset ring-red-500/30',
  gestor: 'bg-blue-500/15 text-blue-600 ring-1 ring-inset ring-blue-500/30',
  lider: 'bg-emerald-500/15 text-emerald-600 ring-1 ring-inset ring-emerald-500/30',
  membro: 'bg-gray-500/15 text-gray-600 ring-1 ring-inset ring-gray-500/30'
}

export function AdminAppPage() {
  const navigate = useNavigate()
  const {
    igrejas,
    usuarios,
    isLoadingIgrejas,
    isLoadingUsuarios,
    errorIgrejas,
    errorUsuarios,
    updateIgreja,
    deleteIgreja,
    updateUsuario,
    deleteUsuario
  } = useAdmin()
  
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTab, setSelectedTab] = useState('igrejas')

  // Filtros
  const igrejasFiltradas = useMemo(() => {
    return igrejas.filter(igreja =>
      igreja.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (igreja.cidade && igreja.cidade.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }, [igrejas, searchTerm])

  const usuariosFiltrados = useMemo(() => {
    return usuarios.filter(usuario =>
      usuario.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      usuario.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (usuario.igreja?.nome && usuario.igreja.nome.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }, [usuarios, searchTerm])

  const handleNovaIgreja = () => {
    navigate('/igreja-nova')
  }

  const handleNovoUsuario = () => {
    navigate('/usuario-novo')
  }

  const handleEditItem = (item: Igreja | UsuarioAdmin) => {
    if ('endereco' in item && 'pastor' in item) {
      // É uma igreja
      navigate(`/igreja/${item.id}/editar`)
    } else if ('permissao' in item) {
      // É um usuário - navegar para página de edição de usuário
      navigate(`/usuario/${item.id}/editar`)
    }
  }

  const handleDeleteItem = (id: string, type: 'igreja' | 'usuario') => {
    if (type === 'igreja') {
      deleteIgreja(id)
    } else {
      deleteUsuario(id)
    }
  }

  const handleToggleStatus = (id: string, type: 'igreja' | 'usuario') => {
    if (type === 'igreja') {
      const igreja = igrejas.find(i => i.id === id)
      if (igreja) {
        updateIgreja({ id, ativa: !igreja.ativa })
      }
    } else {
      const usuario = usuarios.find(u => u.id === id)
      if (usuario) {
        updateUsuario({ id, ativo: !usuario.ativo })
      }
    }
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/70">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Admin App</h1>
            <p className="text-muted-foreground">Gerencie igrejas e usuários do sistema</p>
          </div>
        </div>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar igrejas, usuários..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <CustomTabs 
          tabs={[
            { id: 'igrejas', label: 'Igrejas', count: igrejas.length },
            { id: 'usuarios', label: 'Usuários', count: usuarios.length }
          ]}
          activeTab={selectedTab}
          onTabClick={setSelectedTab}
        />

        {/* Igrejas Tab */}
        {selectedTab === 'igrejas' && (
          <div className="space-y-4 mt-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Igrejas Cadastradas</h2>
              <Button onClick={handleNovaIgreja} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Nova Igreja
              </Button>
            </div>

            <div className="grid gap-4">
              {isLoadingIgrejas ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="ml-2">Carregando igrejas...</span>
                </div>
              ) : errorIgrejas ? (
                <div className="text-center py-8 text-red-600">
                  Erro ao carregar igrejas: {errorIgrejas.message}
                </div>
              ) : igrejasFiltradas.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma igreja encontrada
                </div>
              ) : (
                igrejasFiltradas.map((igreja, index) => (
                <motion.div
                  key={igreja.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold">{igreja.nome}</h3>
                            <Badge variant={igreja.ativa ? 'default' : 'secondary'}>
                              {igreja.ativa ? 'Ativa' : 'Inativa'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            {igreja.endereco || 'Endereço não informado'}, {igreja.cidade || 'Cidade não informada'} - {igreja.estado || 'Estado não informado'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {igreja.email || 'Email não informado'} • {igreja.telefone || 'Telefone não informado'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(igreja.id, 'igreja')}
                          >
                            {igreja.ativa ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditItem(igreja)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteItem(igreja.id, 'igreja')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
              )}
            </div>
          </div>
        )}

        {/* Usuários Tab */}
        {selectedTab === 'usuarios' && (
          <div className="space-y-4 mt-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Usuários Cadastrados</h2>
              <Button onClick={handleNovoUsuario} className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Novo Usuário
              </Button>
            </div>

            <div className="grid gap-4">
              {isLoadingUsuarios ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="ml-2">Carregando usuários...</span>
                </div>
              ) : errorUsuarios ? (
                <div className="text-center py-8 text-red-600">
                  Erro ao carregar usuários: {errorUsuarios.message}
                </div>
              ) : usuariosFiltrados.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum usuário encontrado
                </div>
              ) : (
                usuariosFiltrados.map((usuario, index) => (
                <motion.div
                  key={usuario.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold">{usuario.nome}</h3>
                            <Badge className={usuario.permissao ? PERMISSAO_STYLES[usuario.permissao] : 'bg-gray-500/15 text-gray-600 ring-1 ring-inset ring-gray-500/30'}>
                              {usuario.permissao ? PERMISSAO_LABELS[usuario.permissao] : 'Sem permissão'}
                            </Badge>
                            <Badge variant={usuario.ativo ? 'default' : 'secondary'}>
                              {usuario.ativo ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            {usuario.email} • {usuario.telefone}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Igreja: {usuario.igreja?.nome || 'Sem igreja'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(usuario.id, 'usuario')}
                          >
                            {usuario.ativo ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditItem(usuario)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteItem(usuario.id, 'usuario')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  )
}